import { useState, useRef, useEffect, type ReactNode } from "react";
import { Send, X } from "lucide-react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { EntityActionBar, type ReactionEmoji } from "./entity-action-bar";

interface InteractionFooterProps {
  ownerName: string;
  isRead: boolean;
  isPending?: boolean;
  previewThumbnail: ReactNode;
  onReaction: (emoji: ReactionEmoji) => void;
  onComment: (text: string) => void;
  onMarkAsRead: () => void;
}

export function InteractionFooter({
  ownerName,
  isRead,
  isPending = false,
  previewThumbnail,
  onReaction,
  onComment,
  onMarkAsRead,
}: InteractionFooterProps) {
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentText, setCommentText] = useState("");
  const commentInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (showCommentInput && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [showCommentInput]);

  const handleComment = () => {
    setShowCommentInput(true);
  };

  const handleCloseCommentInput = () => {
    setShowCommentInput(false);
    setCommentText("");
  };

  const handleSubmitComment = () => {
    if (!commentText.trim()) return;
    onComment(commentText.trim());
    setCommentText("");
    setShowCommentInput(false);
  };

  return (
    <div className="border-t border-zinc-200 bg-white p-4">
      {showCommentInput ? (
        <div className="flex flex-col gap-3">
          {/* Reply context */}
          <div className="relative flex items-start gap-3 rounded-lg bg-gray-100 p-3">
            <div className="absolute bottom-3 left-3 top-3 w-1 rounded-full bg-blue-500" />
            <div className="ml-3 flex flex-1 items-start gap-3">
              {previewThumbnail}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-blue-500">
                  {ownerName}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleCloseCommentInput}
              className="flex-shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
              aria-label="Cancel reply"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Comment input */}
          <div className="flex items-end gap-2">
            <textarea
              ref={commentInputRef}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmitComment();
                }
              }}
              placeholder="Write a comment..."
              className="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={2}
            />
            <button
              type="button"
              onClick={handleSubmitComment}
              disabled={!commentText.trim() || isPending}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-500 text-white transition-colors hover:bg-blue-600 disabled:opacity-50"
              aria-label="Send comment"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <TooltipProvider>
          <EntityActionBar
            onReaction={onReaction}
            onComment={handleComment}
            onMarkAsRead={onMarkAsRead}
            isRead={isRead}
          />
        </TooltipProvider>
      )}
    </div>
  );
}
